import { type FormEvent, type JSX, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { RoutingStrategyHelp, StrategyBadge } from '@/components/routing-strategy-help';
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  IconAction,
  Input,
  Skeleton,
  Spinner,
  Textarea,
} from '@/components/ui';
import {
  useCreateRateCard,
  useDeleteRateCard,
  useRateCards,
  useSetDefaultRateCard,
  useUpdateRateCard,
  type RateCard,
  type RateCardStrategy,
} from '@/hooks/useAdminRateCards';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api';
import { can } from '@/lib/permissions';
import { formatRelativeTime } from '@/lib/format';

// ---------------------------------------------------------------------------
// Reseller admin → Rate cards (the NAMED containers). Per ADR-008 each
// card has a routing strategy (lcr | lcd | priority) that tiebreaks
// between competing rates inside it. Rates inside a card are managed at
// /admin/rate-cards/:id.
//
// Tenants attach to cards via the multi-card cascade junction (managed
// from /admin/customers/:id), not via plans anymore.
// ---------------------------------------------------------------------------
export default function AdminRateCardsPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const { user } = useAuth();
  // Operator+ already has read access (sidebar lets them in here);
  // mutations stay admin+ on the backend, so hide the affordances
  // for non-admin so the UI matches the actual permission grant.
  const canEdit = can.useAdminModule(user?.role);
  const list = useRateCards();
  const setDefault = useSetDefaultRateCard();
  const remove = useDeleteRateCard();

  const [editing, setEditing] = useState<RateCard | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<RateCard | null>(null);

  const items = list.data?.items ?? [];

  const openNew = (): void => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (card: RateCard): void => {
    setEditing(card);
    setShowForm(true);
  };
  const closeForm = (): void => {
    setShowForm(false);
    setEditing(null);
  };

  const handleDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.id);
    } catch {
      // refetch surfaces failures inline
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
            {t('arc_title')}
          </h1>
          <p className="text-[13px] text-text-3 mt-1.5">
            {items.length === 0
              ? t('arc_sub_empty')
              : t('arc_sub', { total: items.length.toLocaleString(lang) })}
          </p>
        </div>
        {canEdit && (
          <Button onClick={openNew}>
            <Icon.plus width="14" height="14" />
            {t('arc_new')}
          </Button>
        )}
      </header>

      <Card padding="none">
        {list.isLoading ? (
          <div className="p-5 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-[14px] text-text-2 font-medium">{t('arc_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1 max-w-[460px] mx-auto">
              {t('arc_empty_sub')}
            </p>
            {canEdit && (
              <Button className="mt-5" onClick={openNew}>
                <Icon.plus width="14" height="14" />
                {t('arc_empty_cta')}
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('arc_col_name')}</th>
                  <th className="px-5 py-2.5 font-medium">
                    {t('arc_col_strategy')}
                    <RoutingStrategyHelp className="ml-1.5" />
                  </th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('arc_col_rates')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('arc_col_carriers')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('arc_col_tenants')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('arc_col_updated')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('arc_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <CardRow
                    key={c.id}
                    card={c}
                    lang={lang}
                    onEdit={() => openEdit(c)}
                    onDelete={() => setPendingDelete(c)}
                    onSetDefault={() => void setDefault.mutateAsync(c.id)}
                    setDefaultBusy={setDefault.isPending}
                    canEdit={canEdit}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showForm && <RateCardFormModal editing={editing} onClose={closeForm} />}

      <ConfirmModal
        open={pendingDelete !== null}
        title={t('arc_delete_title')}
        description={
          pendingDelete
            ? t('arc_delete_desc', { name: pendingDelete.name, rates: pendingDelete.rate_count })
            : ''
        }
        confirmLabel={t('arc_delete_action')}
        cancelLabel={t('arc_delete_cancel')}
        tone="danger"
        busy={remove.isPending}
        onConfirm={() => void handleDelete()}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}

interface CardRowProps {
  card: RateCard;
  lang: string;
  onEdit: () => void;
  onDelete: () => void;
  onSetDefault: () => void;
  setDefaultBusy: boolean;
  canEdit: boolean;
}
function CardRow({
  card,
  lang,
  onEdit,
  onDelete,
  onSetDefault,
  setDefaultBusy,
  canEdit,
}: CardRowProps): JSX.Element {
  const { t } = useTranslation('default');
  const navigate = useNavigate();
  return (
    <tr
      className="border-b border-line last:border-0 hover:bg-surface-2 cursor-pointer"
      onClick={() => navigate(`/admin/rate-cards/${card.id}`)}
    >
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          <Link
            to={`/admin/rate-cards/${card.id}`}
            className="text-text font-medium hover:text-volt-700 hover:underline underline-offset-2"
            onClick={(e) => e.stopPropagation()}
          >
            {card.name}
          </Link>
          {card.is_default && <Badge variant="success">{t('arc_default_pill')}</Badge>}
          {/* Wholesale badge is now driven by card_type (the dispatcher-
              relevant semantic), not owner_type (audit only).
              Backwards-compatible: PR 5 backfill set card_type='wholesale'
              for every existing platform-owned card. */}
          {card.card_type === 'wholesale' && (
            <Badge variant="info" title={t('arc_wholesale_help')}>
              {t('arc_wholesale_pill')}
            </Badge>
          )}
        </div>
        {card.description && (
          <div className="text-[12px] text-text-3 mt-0.5">{card.description}</div>
        )}
      </td>
      <td className="px-5 py-3">
        <StrategyBadge strategy={card.strategy} />
      </td>
      <td className="px-5 py-3 num text-right text-text">
        {card.rate_count.toLocaleString(lang)}
      </td>
      <td className="px-5 py-3 num text-right text-text-2">
        {card.carrier_count.toLocaleString(lang)}
      </td>
      <td className="px-5 py-3 num text-right text-text-2">
        {card.tenant_count.toLocaleString(lang)}
      </td>
      <td className="px-5 py-3 text-text-3 text-[12px] whitespace-nowrap">
        {formatRelativeTime(card.updated_at, lang)}
      </td>
      <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
        {canEdit ? (
          <div className="inline-flex gap-0.5">
            {!card.is_default && (
              <IconAction
                icon={Icon.arrowUp}
                label={t('arc_set_default')}
                onClick={onSetDefault}
                disabled={setDefaultBusy}
              />
            )}
            <IconAction icon={Icon.pencil} label={t('arc_edit')} onClick={onEdit} />
            <IconAction
              icon={Icon.trash}
              label={card.is_default ? t('arc_cant_delete_default') : t('arc_delete')}
              onClick={onDelete}
              disabled={card.is_default}
              tone="danger"
            />
          </div>
        ) : (
          <span className="text-text-4 text-[12px]">—</span>
        )}
      </td>
    </tr>
  );
}

interface RateCardFormModalProps {
  editing: RateCard | null;
  onClose: () => void;
}
function RateCardFormModal({ editing, onClose }: RateCardFormModalProps): JSX.Element {
  const { t } = useTranslation('default');
  const create = useCreateRateCard();
  const update = useUpdateRateCard();
  const isEdit = editing !== null;
  const submitting = create.isPending || update.isPending;

  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [isDefault, setIsDefault] = useState(editing?.is_default ?? false);
  const [strategy, setStrategy] = useState<RateCardStrategy>(editing?.strategy ?? 'lcr');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (trimmed === '') {
      setError(t('arc_err_name'));
      return;
    }
    try {
      if (isEdit && editing) {
        await update.mutateAsync({
          id: editing.id,
          input: {
            name: trimmed,
            description: description.trim() || undefined,
            strategy,
          },
        });
      } else {
        await create.mutateAsync({
          name: trimmed,
          description: description.trim() || undefined,
          is_default: isDefault,
          strategy,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('arc_err_generic'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[480px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={(e) => void submit(e)}>
          <div className="px-5 py-4 border-b border-line">
            <h2 className="display text-[16px] font-semibold text-text">
              {isEdit ? t('arc_form_edit_title') : t('arc_form_new_title')}
            </h2>
            <p className="text-[12px] text-text-3 mt-0.5">{t('arc_form_sub')}</p>
          </div>
          <div className="px-5 py-4 space-y-4">
            {error && (
              <div className="px-3 py-2 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
                {error}
              </div>
            )}

            <Field label={t('arc_field_name')}>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('arc_field_name_placeholder')}
                maxLength={120}
                required
              />
            </Field>

            <Field label={t('arc_field_description')}>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('arc_field_description_placeholder')}
                rows={3}
              />
            </Field>

            <Field
              label={
                <span className="inline-flex items-center gap-1.5">
                  {t('arc_field_strategy')}
                  <RoutingStrategyHelp />
                </span>
              }
            >
              <select
                value={strategy}
                onChange={(e) => setStrategy(e.target.value as RateCardStrategy)}
                className="w-full h-9 px-3 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow"
              >
                <option value="lcr">{t('arc_strategy_lcr_label')}</option>
                <option value="lcd">{t('arc_strategy_lcd_label')}</option>
                <option value="priority">{t('arc_strategy_priority_label')}</option>
              </select>
            </Field>

            {!isEdit && (
              <label className="inline-flex items-center gap-2 text-[13px] text-text">
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
                {t('arc_field_is_default')}
              </label>
            )}
          </div>
          <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              {t('arc_form_cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner />
                  <span>{t('arc_form_saving')}</span>
                </>
              ) : isEdit ? (
                t('arc_form_save')
              ) : (
                t('arc_form_create')
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: React.ReactNode;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div>
      <label className="block text-[12px] font-medium text-text-2 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
